<template>
  <BasePage :page-title="__DICT__.actionMenu.keyDist" back-button show-menu>
    <!-- Loading State -->
    <LoadingSpinner v-if="loading" />

    <!-- Main Content -->
    <div v-else class="d-flex flex-column" style="min-height: calc(100vh - 300px)">
      <!-- Progress Bar -->
      <div class="mb-3">
        <ProgressBar
            :percent="(keyData.distributed/keyData.total)*100"
            :total="keyData.total"
            :raw="keyData.distributed"
            :heading="__DICT__.distributed"
        />
      </div>

      <!-- KD-01: status filter. Blocked is a flag (KD-02), so it is its own select, as on admin. -->
      <div v-if="!showingScanner && !showingNameSearch" class="d-flex gap-2 mb-3" data-test="key-filter">
        <select v-model="statusFilter" class="form-select form-select-sm" data-test="key-status-filter"
                :aria-label="__DICT__.keyDistribution.filterStatus">
          <option value="all">{{ __DICT__.keyDistribution.allStatuses }}</option>
          <option v-for="status in KEY_STATUSES" :key="status" :value="status">{{ __DICT__.keyStatus[status] }}</option>
        </select>
        <select v-model="blockedFilter" class="form-select form-select-sm" data-test="key-blocked-filter"
                :aria-label="__DICT__.blocked">
          <option value="all">{{ __DICT__.keyDistribution.blockedAll }}</option>
          <option value="blocked">{{ __DICT__.keyDistribution.blockedOnly }}</option>
          <option value="not_blocked">{{ __DICT__.keyDistribution.notBlocked }}</option>
        </select>
      </div>

      <!-- Buildings Accordion -->
      <div v-if="!showingScanner && !showingNameSearch" class="mb-3">
        <div v-if="filterActive && !visibleBuildings.length" class="text-center text-muted py-3" data-test="key-filter-empty">
          {{ __DICT__.keyDistribution.noMatchingRooms }}
        </div>
        <div class="accordion" id="buildingsAccordion">
          <AccordionItem
              v-for="building in visibleBuildings"
              :key="building.buildingName"
              :id="`building-${btoa(building.buildingName)}`"
              parent-id="buildingsAccordion"
          >
            <template #header>
              <div class="w-100 pe-2">
                <ProgressBar
                    :percent="(building.distributedRooms/building.totalRooms)*100"
                    :total="building.totalRooms - building.readyRooms"
                    :raw="building.distributedRooms"
                    :heading="building.buildingName"
                    :stacked="true"
                    :readyRooms="building.readyRooms"
                    :blockedRooms="building.blockedRooms"
                />
                <div v-if="filterActive" class="small text-muted mt-1" data-test="key-filter-count">
                  {{ getRoomsForBuilding(building.buildingName).length }} {{ __DICT__.keyDistribution.matchingRooms }}
                </div>
              </div>
            </template>
            <ul class="list-group list-group-flush">
              <li
                  v-for="room in getRoomsForBuilding(building.buildingName)"
                  :key="room.roomId"
                  class="list-group-item d-flex justify-content-between align-items-center"
                  data-test="key-room"
                  @click="navigateToRoom(room)"
              >
                <div>
                  <strong>{{ room.roomName }}</strong>
                  <div class="small text-muted">{{ room.numberOfKeys || 0 }} {{ __DICT__.keyDistribution.keys }}</div>
                </div>
                <div class="d-flex gap-1">
                  <span v-if="room.distributed && !room.returned" class="badge bg-success text-white">{{ __DICT__.distributed }}</span>
                  <span v-else-if="room.returned" class="badge bg-purple text-white">{{ __DICT__.returned }}</span>
                  <span v-else class="badge text-white" :class="statusBadgeClass(room)">{{ statusLabel(room) }}</span>
                  <!-- Blocked is independent of status (KD-02), so it is an extra pill. -->
                  <span v-if="room.blocked" class="badge bg-danger text-white">{{ __DICT__.blocked }}</span>
                </div>
              </li>
            </ul>
          </AccordionItem>
        </div>
      </div>

      <!-- KD-05: rooms a permitted rep can mark ready, e.g. a floor handed over at once -->
      <div v-if="canMarkReady && notReadyRooms.length && !showingScanner && !showingNameSearch" class="card mb-3">
        <div class="card-header d-flex justify-content-between align-items-center">
          <h3 class="card-title mb-0">{{ __DICT__.keyDistribution.notReadyRooms }} ({{ notReadyRooms.length }})</h3>
          <button class="btn btn-sm btn-success" :disabled="!selectedNotReady.length || markingReady" @click="markSelectedReady">
            {{ __DICT__.keyDistribution.markSelectedReady }} ({{ selectedNotReady.length }})
          </button>
        </div>
        <ul class="list-group list-group-flush">
          <li v-for="room in notReadyRooms" :key="room.id" class="list-group-item d-flex align-items-center gap-2">
            <input v-model="selectedNotReady" class="form-check-input m-0" type="checkbox" :value="room.id"
                   :disabled="room.blocked === 1 || !room.numberOfKeys">
            <div class="flex-fill" @click="navigateToRoom(room)">
              <strong>{{ room.roomName }}</strong>
              <div class="small text-muted">{{ room.buildingName }} · {{ room.numberOfKeys || 0 }} {{ __DICT__.keyDistribution.keys }}</div>
            </div>
            <span class="badge text-white" :class="statusBadgeClass(room)">{{ statusLabel(room) }}</span>
            <span v-if="room.blocked" class="badge bg-danger text-white">{{ __DICT__.blocked }}</span>
          </li>
        </ul>
      </div>

      <!-- Scanner Component -->
      <QrScanner
          v-if="showingScanner"
          @code-scanned="handleBarcodeScan"
          @invalid-code="handleInvalidCode"
      />

      <!-- Name Search Modal -->
      <NameSearchModal
          :is-open="showingNameSearch"
          :tickets="barcodeSearchData || []"
          :heading="`Find Guest`"
          @close="closeNameSearch"
          @select="handleNameSelection"
          show-room-status
          badge-class="text-white"
      />

      <!-- Error Modal -->
      <div class="modal modal-blur fade" :class="{ 'show': showErrorModal }" tabindex="-1" style="display: block;"
           v-if="showErrorModal">
        <div class="modal-dialog modal-sm modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-body">
              <div class="modal-title text-danger">{{ __DICT__.error }}</div>
              <div>{{ error?.message || 'An error occurred' }}</div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-danger w-100" @click="acknowledgeError">
                {{ __DICT__.ok }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="fixed-bottom fixed-bottom-bar p-3">
        <!-- Main View Buttons -->
        <template v-if="!showingScanner && !showingNameSearch">
          <button class="btn btn-primary w-100 mb-3" @click="startScanner">
            {{ __DICT__.scanBarcode }}
          </button>
          <button class="btn btn-secondary w-100" @click="startNameSearch">
            {{ __DICT__.keyDistribution.selectName }}
          </button>
        </template>

        <!-- Scanner Active State Button -->
        <template v-if="showingScanner">
          <button class="btn btn-secondary w-100" @click="resetState">
            {{ __DICT__.cancel }}
          </button>
        </template>
      </div>
    </div>

    <!-- Modal Backdrop -->
    <div class="modal-backdrop fade" :class="{ 'show': showErrorModal }" v-if="showErrorModal"></div>
  </BasePage>
</template>

<script setup>
import {ref, computed, watch, onMounted, onUnmounted} from "vue";
import {useWeekStore} from '@/stores/week.js';
import {api} from "@/services/api.js";
import BasePage from "@/components/BasePage.vue";
import ProgressBar from "@/components/ProgressBar.vue";
import QrScanner from "@/components/QrScanner.vue";
import NameSearchModal from "@/components/NameSearchModal.vue";
import { AccordionItem, LoadingSpinner } from "@/components/atomic";
import {useRoute, useRouter} from "vue-router";
import {passengerIndex} from "@/services/PassengerIndex.js";
import {useHaptics} from "@/composables/useHaptics.js";
import {keyStatusBadgeClass, keyStatusLabel, matchesKeyFilter, KEY_STATUSES} from "@/utils/keyStatus.js";

const statusLabel = (room) => keyStatusLabel(room, __DICT__);
const statusBadgeClass = (room) => keyStatusBadgeClass(room);

// State Management
const weekStore = useWeekStore();
const { successFeedback, errorFeedback } = useHaptics();
const loading = ref(true);
const keyData = ref({total: 0, distributed: 0, keys: []});
const buildings = ref([]);
const entryMethod = ref(null);
const error = ref(null);
const processing = ref(false);
const showErrorModal = ref(false);
const barcodeSearchData = ref([]);
const route = useRoute();
const router = useRouter();

// Computed Properties
const showingScanner = computed(() => entryMethod.value === 'scan');
const showingNameSearch = computed(() => entryMethod.value === 'manual');

// Efficient barcode to room mapping
const barcodeToRoomMap = computed(() => {
  const map = new Map();

  keyData.value.keys.forEach(room => {
    if (room.allowedBarcodes) {
      const barcodes = room.allowedBarcodes.split(',');
      barcodes.forEach(barcode => {
        map.set(barcode, room);
      });
    }
  });

  return map;
});

const btoa = (str) => {
  return window.btoa(str);
}

// KD-01: status / blocked filter, kept for the session so it survives opening a room and coming back.
const FILTER_KEY = 'keyDist.filter';
const readFilter = () => {
  try { return JSON.parse(sessionStorage.getItem(FILTER_KEY) || '{}'); } catch { return {}; }
};
const savedFilter = readFilter();
const statusFilter = ref(KEY_STATUSES.includes(savedFilter.status) ? savedFilter.status : 'all');
const blockedFilter = ref(['blocked', 'not_blocked'].includes(savedFilter.blocked) ? savedFilter.blocked : 'all');
const filterActive = computed(() => statusFilter.value !== 'all' || blockedFilter.value !== 'all');
watch([statusFilter, blockedFilter], ([status, blocked]) => {
  try { sessionStorage.setItem(FILTER_KEY, JSON.stringify({status, blocked})); } catch { /* private mode */ }
});

// Methods
const getRoomsForBuilding = (buildingName) => {
  const filter = {status: statusFilter.value, blocked: blockedFilter.value};
  return keyData.value.keys.filter(key => key.buildingName === buildingName && matchesKeyFilter(key, filter));
};

// A building with no room passing the filter is hidden rather than shown empty.
const visibleBuildings = computed(() => {
  if (!filterActive.value) return buildings.value;
  return buildings.value.filter(building => getRoomsForBuilding(building.buildingName).length > 0);
});

const navigateToRoom = (room, scanType = "manual") => {
  router.push({
    name: 'distKey',
    params: {
      roomId: room.roomId,
      scanType: scanType
    }
  });
};

const startScanner = () => {
  entryMethod.value = 'scan';
};

const startNameSearch = async () => {
  entryMethod.value = 'manual';

  // First ensure we have loaded the week data for passengers
  const passengerData = await weekStore.weekData;

  // Prepare barcode search data
  const searchEntries = [];

  // Iterate through all eligible keys
  keyData.value.keys.filter(key =>
      //key.allowedBarcodes && key.ready === 1 && key.blocked === 0 && !key.distributed
      true
  ).forEach(key => {
    // Skip if no allowed barcodes
    if (!key.allowedBarcodes) return;

    // Split all barcodes for this room
    const barcodes = key.allowedBarcodes.split(',');

    // For each barcode, create a search entry with the proper booking reference
    barcodes.forEach(barcode => {
      // Extract booking reference (parts 0 and 1 when split by hyphen)
      const parts = barcode.split('-');
      const bookingRef = parts.length >= 2 ? `${parts[0]}-${parts[1]}` : barcode;

      // Find matching passenger info using O(1) indexed lookup
      const passenger = passengerIndex.getByReference(bookingRef);

      searchEntries.push({
        name: passenger ? passenger.passengerName : key.roomName, // Use passenger name if found, fallback to room name
        bookingRef: bookingRef,
        barcode: barcode,
        roomId: key.roomId,
        distributed: key.distributed === 1
      });
    });
  });

  barcodeSearchData.value = searchEntries;
};

const closeNameSearch = () => {
  resetState();
};

const resetState = () => {
  entryMethod.value = null;
  processing.value = false;
};

const handleBarcodeScan = async (barcode) => {
  if (processing.value) return;
  processing.value = true;
  error.value = null;

  try {
    // Look up room by barcode using our map (much faster than array.find)
    const matchingRoom = barcodeToRoomMap.value.get(barcode);

    if (matchingRoom) {
      await router.push(`/keyDist/room/${matchingRoom.roomId}/scan/${barcode}`);
    } else {
      // Fallback to API if no local match
      const response = await api.get(`/api/keyDist/barcode/${barcode}/week/${weekStore.weekId}`);

      if (!response.success) {
        throw new Error(response.error || 'No room found for this barcode');
      }
      await router.push(`/keyDist/room/${response.room.roomId}/scan/${barcode}`);
    }
  } catch (e) {
    error.value = e;
    showErrorModal.value = true;
  } finally {
    processing.value = false;
  }
};

const handleNameSelection = async (selectedItem) => {
  entryMethod.value = null;
  const matchingRoom = barcodeToRoomMap.value.get(selectedItem);
  await router.push(`/keyDist/room/${matchingRoom.roomId}/manual/${selectedItem}`);
};

const handleInvalidCode = () => {
  error.value = {message: 'Invalid barcode format'};
  showErrorModal.value = true;
  errorFeedback();
};

const acknowledgeError = () => {
  showErrorModal.value = false;
  error.value = null;
};

const refreshData = async () => {
  try {
    if(route.params.distLocation) {
      if (route.params.groupType === 'sup') {
        keyData.value = await api.get(`/api/keyDist/allKeys/supplier/${weekStore.weekId}/${route.params.distLocation}`);
        const buildingsResponse = await api.get(`/api/keyDist/buildings/supplier/${weekStore.weekId}/${route.params.distLocation}`);
        if (buildingsResponse.success) {
          buildings.value = buildingsResponse.buildings;
        }
      } else if (route.params.groupType === 'bld') {
        keyData.value = await api.get(`/api/keyDist/allKeys/building/${weekStore.weekId}/${route.params.distLocation}`);
        const buildingsResponse = await api.get(`/api/keyDist/buildings/building/${weekStore.weekId}/${route.params.distLocation}`);
        if (buildingsResponse.success) {
          buildings.value = buildingsResponse.buildings;
        }
      } else {
        keyData.value = await api.get(`/api/keyDist/allKeys/${weekStore.weekId}/${route.params.distLocation}`);
        const buildingsResponse = await api.get(`/api/keyDist/buildings/${weekStore.weekId}/${route.params.distLocation}`);
        if (buildingsResponse.success) {
          buildings.value = buildingsResponse.buildings;
        }
      }
    }
  } catch (error) {
    console.error('Error refreshing data:', error);
  }
};

let refreshTimeout = null;
const refreshDataHook = () => {
  refreshData();
  refreshTimeout = setTimeout(refreshDataHook, 100000);
};

// Initial Data Load
// KD-05: level 3 reps and week admins for the week can mark
// rooms ready. The API decides; this only hides what would be refused.
const canMarkReady = ref(false);
const notReadyRooms = ref([]);
const selectedNotReady = ref([]);
const markingReady = ref(false);

const loadNotReadyRooms = async () => {
  if (!canMarkReady.value || !route.params.distLocation) return;
  const mode = route.params.groupType === 'sup' ? 'supplier' : route.params.groupType === 'bld' ? 'building' : 'location';
  try {
    const response = await api.get(`/api/keyDist/notReady/${weekStore.weekId}?mode=${mode}&distLoc=${route.params.distLocation}`);
    notReadyRooms.value = response.rooms || [];
    selectedNotReady.value = selectedNotReady.value.filter(id => notReadyRooms.value.some(r => r.id === id));
  } catch (err) {
    console.error('Error loading not-ready rooms:', err);
  }
};

const markSelectedReady = async () => {
  markingReady.value = true;
  try {
    await api.post('/api/keyDist/status', {weekId: weekStore.weekId, ids: selectedNotReady.value, status: 'ready'});
    successFeedback();
    selectedNotReady.value = [];
    await Promise.all([refreshData(), loadNotReadyRooms()]);
  } catch (err) {
    errorFeedback();
    error.value = err;
    showErrorModal.value = true;
  } finally {
    markingReady.value = false;
  }
};

onMounted(async () => {
  try {
    await refreshData();
    refreshDataHook();
    try {
      const permission = await api.get(`/api/keyDist/canMarkReady/${weekStore.weekId}`);
      canMarkReady.value = !!permission.allowed;
    } catch {
      canMarkReady.value = false;
    }
    await loadNotReadyRooms();
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }
});
</script>

<style scoped>
/* Ensure badges have white text */
.badge {
  color: white !important;
}

/* Make list items clickable */
.list-group-item {
  cursor: pointer;
  transition: background-color 0.2s;
}

.list-group-item:hover {
  background-color: rgba(var(--tblr-body-color-rgb), 0.03);
}

/* Improve modal appearance */
.modal-content {
  border: none;
  box-shadow: var(--tblr-box-shadow-lg);
}

/* Progress bar styling */
:deep(.progress) {
  height: 0.8rem;
  border-radius: 0.5rem;
}

/* Card styling */
.card {
  box-shadow: var(--tblr-box-shadow-sm);
  border: none;
}

.card-header {
  background-color: var(--tblr-bg-surface);
  border-bottom: 1px solid var(--tblr-border-color);
  position: relative;
}

.card-status-top {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
}
</style>
